// src/ParentDashboard.js
// ============================================================
// ScholarSync AI — Parent Dashboard
// ============================================================
// Shows linked children with "View Dashboard" links.
// Entry point for parents after login/registration.
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import AppHeader from './AppHeader';

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents]   = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [planType, setPlanType]   = useState(null);

  // Fetch plan type for button logic
  useEffect(() => {
    fetch(`${API_URL}/api/stripe/subscription-status`, {
      headers: { 'Authorization': `Bearer ${user.token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.planType) setPlanType(data.planType); })
      .catch(() => {});
  }, [user]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/parent-links/my-students`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setStudents(data.students);
    } catch (err) {
      setError('Unable to load linked students.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Fetch summary stats for each linked student
  useEffect(() => {
    if (students.length === 0) return;
    const fetchSummaries = async () => {
      const results = {};
      await Promise.all(
        students.map(async (s) => {
          try {
            const res = await fetch(
              `${API_URL}/api/parent-links/student-summary/${s.student_id}`,
              { headers: { 'Authorization': `Bearer ${user.token}` } }
            );
            if (res.ok) {
              results[s.student_id] = await res.json();
            }
          } catch (err) {
            console.error(`Summary fetch failed for ${s.student_id}:`, err);
          }
        })
      );
      setSummaries(results);
    };
    fetchSummaries();
  }, [students, user]);

    if (loading) {
    return (
      <>
        <AppHeader subtitle="Parent Dashboard" />
        <div style={styles.page}>
          <p style={styles.loadingText}>Loading your dashboard…</p>
        </div>
      </>
    );
  }

    return (
    <>
      <AppHeader subtitle="Parent Dashboard" />
      <div style={styles.page}>
      <div style={styles.container}>
      <h1 style={styles.title}>Your Students</h1>

        {error && <div style={styles.error}>{error}</div>}

        {/* Linked Students */}
        <div style={styles.section}>
                    <div style={styles.sectionHeader}>
            <div />
                       {planType === 'individual' && students.length >= 1 ? (
              <button
                onClick={() => navigate('/parent/upgrade')}
                style={{ ...styles.addBtn, background: '#2563EB' }}
              >
                Upgrade to Family Plan
              </button>
                        ) : planType === 'family' && students.length >= 3 ? (
              <a
                href="mailto:theteam@yourscholarshipfinder.com?subject=Family%20plan%20—%20additional%20students"
                style={{ ...styles.addBtn, textDecoration: 'none', display: 'inline-block' }}
              >
                Contact Us to Add More
              </a>
            ) : (
              <button
                onClick={() => navigate('/parent/link-student')}
                style={styles.addBtn}
              >
                + Link a Student
              </button>
            )}
          </div>

          {students.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyTitle}>No students linked yet</p>
              <p style={styles.emptyText}>
                Link your child's account to view their scholarship matches and application progress.
              </p>
                            <button
                onClick={() => navigate('/parent/link-student')}
                style={styles.emptyBtn}
              >
                + Link a Student
              </button>
            </div>
          ) : (
            <div style={styles.cardGrid}>
              {students.map((s) => {
                const sum = summaries[s.student_id];
                return (
                  <div key={s.student_id} style={styles.card}>
                    <div style={styles.cardTop}>
                      <div style={styles.avatar}>
                        {s.full_name ? s.full_name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div>
                        <h3 style={styles.cardName}>{s.full_name}</h3>
                        <p style={styles.cardDetail}>
                          {s.state_of_residence || 'State not set'}
                          {s.graduation_date
                            ? ` · Class of ${new Date(s.graduation_date).getFullYear()}`
                            : ''}
                        </p>
                      </div>
                    </div>

                    {/* Summary stats */}
                    {sum ? (
                      <>
                        <div style={styles.statsRow}>
                          <div style={styles.statBox}>
                            <div style={styles.statNumber}>{sum.matchCount}</div>
                            <div style={styles.statLabel}>Matches</div>
                          </div>
                          <div style={styles.statBox}>
                            <div style={{...styles.statNumber, color: '#059669'}}>
                              ${(() => {
                                const v = Number(sum.totalPotentialValue || 0);
                                if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
                                if (v >= 1000) return Math.round(v).toLocaleString();
                                return v;
                              })()}
                            </div>
                            <div style={styles.statLabel}>Potential Value</div>
                          </div>
                          <div style={styles.statBox}>
                            <div style={{...styles.statNumber, color: '#2563EB'}}>
                              {sum.trackerCounts.in_progress}
                            </div>
                            <div style={styles.statLabel}>In Progress</div>
                          </div>
                          <div style={styles.statBox}>
                            <div style={{...styles.statNumber, color: '#7C3AED'}}>
                              {sum.trackerCounts.submitted}
                            </div>
                            <div style={styles.statLabel}>Submitted</div>
                          </div>
                          <div style={styles.statBox}>
                            <div style={{...styles.statNumber, color: '#D97706'}}>
                              {sum.trackerCounts.awarded}
                            </div>
                            <div style={styles.statLabel}>Awarded</div>
                          </div>
                        </div>

                        {/* Upcoming deadlines */}
                        {sum.upcomingDeadlines.length > 0 && (
                          <div style={styles.deadlineSection}>
                            <div style={styles.deadlineTitle}>Upcoming Deadlines</div>
                            {sum.upcomingDeadlines.map((d, i) => (
                              <div key={i} style={styles.deadlineRow}>
                                <span style={styles.deadlineName}>{d.name}</span>
                                <span style={styles.deadlineDate}>
                                  {new Date(d.deadline).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric'
                                  })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={styles.statsLoading}>Loading stats…</div>
                    )}

                    <Link
                      to={`/dashboard/${s.student_id}`}
                      style={styles.viewBtn}
                    >
                      View Scholarship Dashboard →
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
        </div>
    </>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#F3F4F6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '20px',
  },
  container: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    paddingTop: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginTop: '4px',
  },
  logoutBtn: {
    padding: '8px 16px',
    background: '#fff',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#6B7280',
    cursor: 'pointer',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    padding: '12px',
    color: '#DC2626',
    fontSize: '13px',
    marginBottom: '20px',
  },
  section: {
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    margin: 0,
  },
  addBtn: {
    padding: '8px 16px',
    background: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  emptyState: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '20px',
    maxWidth: '400px',
    margin: '0 auto 20px',
    lineHeight: '1.5',
  },
  emptyBtn: {
    padding: '10px 24px',
    background: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  cardGrid: {
    display: 'grid',
    gap: '16px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '16px',
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: '#E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    color: '#374151',
    flexShrink: 0,
  },
  cardName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  cardDetail: {
    fontSize: '13px',
    color: '#6B7280',
    marginTop: '2px',
  },
  viewBtn: {
    display: 'block',
    textAlign: 'center',
    padding: '10px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    color: '#111827',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
    paddingTop: '100px',
  },
  statsRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  statBox: {
    flex: 1,
    background: '#F9FAFB',
    borderRadius: '8px',
    padding: '12px 8px',
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '11px',
    color: '#6B7280',
    marginTop: '4px',
    fontWeight: '500',
  },
  statsLoading: {
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: '13px',
    padding: '16px 0',
  },
  deadlineSection: {
    borderTop: '1px solid #F3F4F6',
    paddingTop: '12px',
    marginBottom: '16px',
  },
  deadlineTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  deadlineRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
  },
  deadlineName: {
    fontSize: '13px',
    color: '#374151',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: '12px',
  },
  deadlineDate: {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: '500',
    flexShrink: 0,
  },
};